import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { GoogleLogin } from '@react-oauth/google';
import CustomInput from '../../components/Input';
import Button from '../../components/Button';
import AuthMarketingPanel from '../../components/AuthMarketingPanel';
import FruitRain from '../../components/FruitRain';
import { authLoginUrl, authGoogleLoginUrl } from '../../constants/urls';
import { useCsrfToken } from '../../hooks/useCsrfToken';
import { useToast } from '../../context/ToastContext';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const csrfToken = useCsrfToken();
  const { showToast } = useToast();
  const from = location.state?.from?.pathname || '/';
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const googleButtonContainerRef = useRef(null);
  const [googleButtonWidth, setGoogleButtonWidth] = useState(null);

  useEffect(() => {
    const updateWidth = () => {
      if (googleButtonContainerRef.current) {
        setGoogleButtonWidth(Math.min(googleButtonContainerRef.current.offsetWidth, 400));
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const redirectAfterLogin = () => {
    showToast('Ingreso exitoso', { variant: 'success' });
    navigate(from, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(
        authLoginUrl,
        {
          email: formData.email,
          password: formData.password,
        },
        {
          withCredentials: true,
          headers: {
            'X-CSRFToken': csrfToken,
          },
        }
      );

      redirectAfterLogin();
    } catch (err) {
      if (err.response && err.response.data) {
        const data = err.response.data;
        if (typeof data === 'string') {
          setError(data);
        } else if (data.non_field_errors && data.non_field_errors.length > 0) {
          setError(data.non_field_errors[0]);
        } else if (data.detail) {
          setError(data.detail);
        } else {
          setError('Error al iniciar sesión. Intente nuevamente.');
        }
      } else {
        setError('Error de conexión. Intente nuevamente.');
      }
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        authGoogleLoginUrl,
        { credential: credentialResponse.credential },
        {
          withCredentials: true,
          headers: {
            'X-CSRFToken': csrfToken,
          },
        }
      );

      if (response.status === 201) {
        showToast(response.data.message, { variant: 'success', duration: 8000 });
        setLoading(false);
        return;
      }

      redirectAfterLogin();
    } catch (err) {
      if (err.response && err.response.data && err.response.data.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Error al iniciar sesión con Google. Intente nuevamente.');
      }
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-r from-blue-lahuerta to-little-blue-lahuerta p-4">
      <FruitRain />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_65%_at_center,rgba(74,123,196,0.45)_0%,rgba(74,123,196,0)_70%)]"
      />
      <div className="relative z-10 w-full max-w-6xl flex min-h-[600px] bg-white rounded-xl shadow-xl overflow-hidden">
      <AuthMarketingPanel
        titleLine1="Bienvenido a"
        titleLine2="La Huerta 👋"
        description="Gestioná clientes, productos y operaciones del negocio de forma simple. Automatizá tareas repetitivas y ganá tiempo para lo importante."
      />

      {/* Panel derecho - formulario de login */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-[400px]">
          <h1 className="text-3xl font-bold mb-2 text-gray-900">Iniciar sesión</h1>
          <p className="text-sm text-gray-500 mb-6">
            Ingresá tus credenciales para acceder al panel de La Huerta.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <CustomInput
              name="email"
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="email"
            />

            <CustomInput
              name="password"
              label="Contraseña"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
            />

            {error && (
              <div className="text-red-600 text-sm mt-2">
                {error}
              </div>
            )}

            <div className="mt-6">
              <Button
                label={loading ? 'Ingresando...' : 'Ingresar'}
                color="primary"
                variant="contained"
                size="large"
                type="submit"
                disabled={loading || !formData.email || !formData.password}
                className="w-full !justify-center"
              />
            </div>

            <div className="mt-4" ref={googleButtonContainerRef}>
              {googleButtonWidth && (
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => setError('Error al iniciar sesión con Google. Intente nuevamente.')}
                  width={googleButtonWidth}
                  size="large"
                  shape="rectangular"
                />
              )}
            </div>

            <div className="mt-4 flex flex-col items-center space-y-2">
              <button
                type="button"
                className="text-sm hover:underline"
                style={{ color: '#4a7bc4', background: 'none', border: 'none', cursor: 'pointer' }}
                onClick={() => navigate('/password-reset')}
              >
                ¿Olvidaste tu contraseña?
              </button>

              <button
                type="button"
                className="text-sm hover:underline"
                style={{ color: '#4a7bc4', background: 'none', border: 'none', cursor: 'pointer' }}
                onClick={() => navigate('/register')}
              >
                ¿Sos nuevo? Registrate
              </button>
            </div>
          </form>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Login;


